import { useState, useEffect } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import * as z from 'zod'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Button } from '@/components/ui/button'
import { Mail } from 'lucide-react'

interface PasscodeDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  onSuccess: () => void
}

const passcodeSchema = z.object({
  passcode: z.string().min(1, 'Passcode is required'),
})

type PasscodeFormData = z.infer<typeof passcodeSchema>

const CORRECT_PASSCODE = 'EndHepCTogether'

export function PasscodeDialog({ open, onOpenChange, onSuccess }: PasscodeDialogProps) {
  const [error, setError] = useState('')

  const { register, handleSubmit, reset, formState: { errors: formErrors, isSubmitting } } = useForm<PasscodeFormData>({
    resolver: zodResolver(passcodeSchema),
  })

  // Reset form when modal closes
  useEffect(() => {
    if (!open) {
      reset()
      setError('')
    }
  }, [open, reset])

  const onSubmit = async (formData: PasscodeFormData) => {
    setError('')

    if (formData.passcode === CORRECT_PASSCODE) {
      onSuccess()
      onOpenChange(false)
    } else {
      setError('Incorrect passcode. Please try again.')
    }
  }

  const handleEmailRequest = () => {
    window.location.href = 'mailto:info@breakthroughhealth.app?subject=Access Request for Treatment Platform'
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader className="text-center">
          <DialogTitle className="text-2xl">Platform Access</DialogTitle>
          <DialogDescription>
            This platform is restricted to authorized healthcare providers.
            Please enter your passcode to continue.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          {error && (
            <div className="rounded-md bg-destructive/15 p-3 text-sm text-destructive">
              {error}
            </div>
          )}

          <div className="space-y-2">
            <Label htmlFor="passcode">Access Passcode</Label>
            <Input
              id="passcode"
              type="password"
              placeholder="Enter passcode"
              {...register('passcode')}
            />
            {formErrors.passcode && (
              <p className="text-sm text-destructive">{formErrors.passcode.message}</p>
            )}
          </div>

          <Button type="submit" className="w-full" disabled={isSubmitting}>
            Continue
          </Button>

          <div className="relative">
            <div className="absolute inset-0 flex items-center">
              <span className="w-full border-t" />
            </div>
            <div className="relative flex justify-center text-xs uppercase">
              <span className="bg-background px-2 text-muted-foreground">
                Don't have access?
              </span>
            </div>
          </div>

          <Button
            type="button"
            variant="outline"
            className="w-full"
            onClick={handleEmailRequest}
          >
            <Mail className="mr-2 h-4 w-4" />
            Request Access
          </Button>

          <p className="text-xs text-center text-muted-foreground">
            Click above to email us at info@breakthroughhealth.app
          </p>
        </form>
      </DialogContent>
    </Dialog>
  )
}
